/**
 * Pre-upload preflight validation for `createrouteversion`.
 *
 * Hailer's `createrouteversion` use-case collapses SIX distinct failure points
 * (fetch/create Route, create RouteVersion, create RouteShape, create RouteStops
 * [new]/[existing], update name) into a single opaque string —
 * "Creating new RouteVersion failed" — and only logs the real cause server-side.
 * For brand-new sim routes (e.g. "DAY 1"/"DAY 10") every stop is inserted inline,
 * so a NOT NULL Address/FixedRouteStop column violation (non-finite or (0,0)
 * coordinates, empty label) or an empty route shape surfaces as that generic
 * message with no client-side signal.
 *
 * This module fails fast BEFORE the network call with a precise, per-stop
 * message (which route, which stop, why) so the operator can fix the data rather
 * than guessing from Hailer's catch-all. It is pure and side-effect free for unit
 * testing (no React, no Ops client).
 *
 * See investigation findings: FAILURE (1), causes #1 (Hailer swallows the error)
 * and #2 (new-stop inline insert is the most likely trigger; the existing coord
 * guard is NaN-permissive because it uses `typeof === 'number'` + range checks
 * that NaN passes silently). This helper uses `Number.isFinite`, so NaN/Infinity
 * are rejected.
 */

import type { ExportedRouteData } from '../fixedRouteDataExporter';

/** A single reason a route cannot be safely uploaded, with display context. */
export interface PreflightIssue {
  /** Atlas route id. */
  routeId: string;
  /** Route display name. */
  routeName: string;
  /** Precise, human-readable reason (route + stop + why). */
  message: string;
}

export interface PreflightResult {
  ok: boolean;
  issues: PreflightIssue[];
}

/** Minimum stops a route must have for Hailer to create a valid version. */
const MIN_STOPS = 2;

/**
 * True when a coordinate value is a real, in-range number. Uses
 * `Number.isFinite` (NOT `typeof === 'number'`) so NaN/Infinity are rejected —
 * those pass `typeof number` AND pass range comparisons (`NaN < -90` is false),
 * which is exactly how non-finite coordinates leaked into the inline Address
 * INSERT and triggered Hailer's generic failure.
 */
function isFiniteInRange(value: number, min: number, max: number): boolean {
  return Number.isFinite(value) && value >= min && value <= max;
}

/** (0,0) Null Island — almost always a missing/defaulted coordinate, not a real stop. */
function isNullIsland(lat: number, lng: number): boolean {
  return lat === 0 && lng === 0;
}

/**
 * Validate one route's exported data immediately before `createRouteVersion`.
 *
 * Checks (each produces a precise message naming the route + offending stop):
 *  - at least {@link MIN_STOPS} stops,
 *  - every stop has finite, in-range, non-(0,0) coordinates,
 *  - a non-empty route shape (the auto-routing step in `useUploadRoutes` should
 *    have populated this; an empty shape here means it could not).
 *
 * Labels are intentionally NOT checked — the exporter always produces a
 * non-empty label (coordinate fallback), so the Hailer NOT NULL label column is
 * never violated once coordinates are valid; see the inline note in the loop.
 *
 * Stop numbers in messages are 1-based to match how operators count stops.
 */
export function preflightCheckRoute(exported: ExportedRouteData): PreflightResult {
  const routeId = exported.route?.id ?? 'unknown';
  const routeName = exported.routeMetadata?.name || exported.route?.name || 'Unnamed route';
  const issues: PreflightIssue[] = [];
  const push = (message: string) => issues.push({ routeId, routeName, message });

  const stops = exported.stops ?? [];

  if (stops.length < MIN_STOPS) {
    push(
      `Route "${routeName}" has ${stops.length} stop(s); at least ${MIN_STOPS} are required to create a route in Ops.`,
    );
  }

  stops.forEach((stop, index) => {
    const stopNo = index + 1;
    const label = (stop.stopName ?? '').trim();

    // NOTE on labels: we deliberately do NOT reject stops that lack a
    // name/externalReference/address. The exporter (`transformStopForPayload`)
    // ALWAYS emits a non-empty `label` — its hierarchy ends in a "<lat>, <lng>"
    // coordinate fallback — so the Hailer `FixedRouteStop.label` NOT NULL column
    // is never violated by this path once coordinates are valid (which we check
    // below). Flagging a label-less stop here would be a FALSE POSITIVE that
    // blocks an upload Hailer would have accepted (it would label the stop by its
    // coordinates). Missing human names are a data-quality nudge handled by the
    // existing upload warnings, not a hard createrouteversion failure.

    const coords = stop.coordinates;
    if (
      !coords ||
      !isFiniteInRange(coords.lat, -90, 90) ||
      !isFiniteInRange(coords.lng, -180, 180)
    ) {
      push(
        `Route "${routeName}": stop #${stopNo}${label ? ` ("${label}")` : ''} has invalid coordinates ` +
          `(lat=${coords?.lat}, lng=${coords?.lng}); cannot create stop in Ops.`,
      );
    } else if (isNullIsland(coords.lat, coords.lng)) {
      push(
        `Route "${routeName}": stop #${stopNo}${label ? ` ("${label}")` : ''} is at (0, 0) (Null Island); ` +
          `this is almost always a missing coordinate. Fix the stop location before uploading.`,
      );
    }
  });

  const shape = exported.routeShape ?? [];
  if (shape.length === 0) {
    push(
      `Route "${routeName}" has no road geometry (route shape is empty); auto-routing must succeed before upload.`,
    );
  }

  return { ok: issues.length === 0, issues };
}
